import React, { useEffect, useState } from "react";
import axios from "axios";
import { ACTIVE_COUNTRY_STORAGE_KEY } from "../App";

interface Country {
  id: number;
  name: string;
  code: string;
  is_sensitive?: boolean;
}

/**
 * Country switcher — shows the user's assigned countries and lets them switch,
 * one at a time. The switch is enforced server-side; this is just the control.
 * Renders nothing for users with no country assignments.
 */
export default function CountrySwitcher() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get("/api/auth/my-countries")
      .then((r) => {
        const d = r.data.data || r.data;
        setCountries(d.countries || []);
        setActive(d.activeCountryId ?? null);
      })
      .catch(() => {});
  }, []);

  if (countries.length === 0) return null;

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : null;
    setBusy(true);
    try {
      await axios.post("/api/auth/switch-country", { country_id: val });
      // Store the new country for THIS tab specifically (sessionStorage,
      // not shared with other tabs) before reloading, so the reload's
      // requests immediately carry the right X-Active-Country-Id header
      // instead of racing whatever another tab last wrote server-side.
      if (val) {
        sessionStorage.setItem(ACTIVE_COUNTRY_STORAGE_KEY, String(val));
      } else {
        sessionStorage.removeItem(ACTIVE_COUNTRY_STORAGE_KEY);
      }
      // Reload so all data is fetched in the new country context.
      window.location.reload();
    } catch {
      setBusy(false);
      alert("Could not switch country. You may not be assigned to it.");
    }
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginRight: 14,
        background: "#0d2e2a",
        padding: "5px 10px",
        borderRadius: 6,
      }}
    >
      <span style={{ color: "#9fc3bc", fontSize: 12 }}>Country:</span>
      <select
        aria-label="Country"
        value={active ?? ""}
        disabled={busy}
        onChange={onChange}
        style={{
          background: "#15403a",
          color: "#fff",
          border: "1px solid #3a6b63",
          borderRadius: 4,
          padding: "3px 6px",
          fontSize: 13,
        }}
      >
        {countries.length > 1 && <option value="">— select a country —</option>}
        {countries.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {c.is_sensitive ? " ⚠" : ""}
          </option>
        ))}
      </select>
    </span>
  );
}
